import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { useQuery, useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import { useRecoilValue } from 'recoil';

import routes from '@/services/routes';
import {
    handleFetchErrors,
    onCompletedHandler,
    onErrorHandler,
} from '@/services/apollo';
import Loader from '@/components/commons/loader';
import { userAtom } from '@/components/with_auth/State';
import { handleColumnSort, SortDirection } from '@/services/sorting';
import Pencil from '@/components/commons/icons/Pencil';
import TrashBin from '@/components/commons/icons/TrashBin';
import ConfirmActionModal from '@/components/settings/ConfirmActionModal';
import Plus from '@/components/commons/icons/Plus';
import Button, { ButtonSize, ButtonType } from '@/components/commons/button';
import SmartTable from '@/components/commons/table/SmartTable';

import { FOLDER_CONTENTS_OPTIONS } from './Constants';
import { FETCH_MONITORED_LABELS, DELETE_MONITORED_LABEL } from './Actions';
import styles from './MonitoredLabels.module.css';

export const DEFAULT_SORT_COLUMN = 'name';
export const DEFAULT_NAME_SORT_DIRECTION = SortDirection.ASC;

const fetchFeatureLabel = (folderContentsValue) => {
    let label = '';
    FOLDER_CONTENTS_OPTIONS.forEach((folderContents) => {
        if (folderContents.value === folderContentsValue) { label = folderContents.label; }
    });

    return label;
};

const folderContentsColumn = {
    label: 'Folder Contents',
    id: 'folderContents',
    render: (rowId, record) => (
        <div key={rowId} className="text-base">
            {fetchFeatureLabel(record.folderContents)}
        </div>
    ),
};

const mailboxColumn = {
    label: 'Mailbox',
    id: 'account',
    render: (rowId, record) => (
        <div key={rowId} className="text-base">
            {record.accounts.map(({ label }, _index) => (
                <div
                    key={`${rowId}-${label}`}
                    className={`${styles.tag}`}
                >
                    {label}
                </div>
            ))}
        </div>
    ),
};

const autopilotColumn = {
    label: 'Autopilot',
    id: 'autopilot',
    render: (rowId, record) => {
        const message = record.dontMove ? 'Off' : 'On';
        return (
            <div key={rowId} className="text-base">
                <p>{message}</p>
            </div>
        );
    },
};

function MonitoredLabelsTable({ setLoading, formError, setFormError }) {
    const router = useRouter();
    const user = useRecoilValue(userAtom);
    const [monitoredLabels, setMonitoredLabels] = useState([]);
    const [isError, setIsError] = useState(false);
    const [sortColumn, setSortColumn] = useState(DEFAULT_SORT_COLUMN);
    const [sortDirection, setSortDirection] = useState(DEFAULT_NAME_SORT_DIRECTION);

    const [modalState, setModalState] = useState({ show: false, action: null });
    const [activeRecord, setActiveRecord] = useState(null);

    const {
        loading, refetch,
    } = useQuery(FETCH_MONITORED_LABELS, {
        variables: {
            id: null,
            sortColumn,
            sortDirection,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
        onError: onErrorHandler(setIsError),
        onCompleted: onCompletedHandler(setIsError, (data) => {
            const labelData = data.user.monitoredLabels.map((monitoredLabel) => ({
                id: monitoredLabel.id,
                name: monitoredLabel.name,
                folderContents: monitoredLabel.folderContents,
                autoCreated: monitoredLabel.autoCreated,
                autoActive: monitoredLabel.autoActive,
                addNewAccounts: monitoredLabel.addNewAccounts,
                archivalMode: monitoredLabel.archivalMode,
                archivalPeriod: monitoredLabel.archivalPeriod,
                dontMove: monitoredLabel.dontMove,
                accounts: monitoredLabel.instances
                    .map((instance) => ({
                        value: instance.account.name,
                        label: instance.account.name,
                    }))
                    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())),
            }));

            setMonitoredLabels(labelData);
            setLoading(false);
        }),
    });

    const [deleteMonitoredLabel] = useMutation(DELETE_MONITORED_LABEL);

    const handleDeleteMonitoredLabel = async (record) => {
        try {
            setLoading(true);
            const response = await deleteMonitoredLabel({
                variables: {
                    deleteMonitoredLabelInput: {
                        id: record.id,
                    },
                },
            });

            if (!response.errors) {
                refetch();
                if (formError !== '') setFormError('');
                toast.success('Folder removed successfully.');
                return;
            }

            setFormError('Something went wrong.  Please try again.');
            setLoading(false);
        } catch (err) {
            setLoading(false);
            handleFetchErrors(err);
            setFormError('An unexpected error occurred.  Please try again later.');
        }
    };

    const openDeleteMonitoredLabelModal = async (record) => {
        setModalState({ show: true, action: null });
        setActiveRecord(record);
    };

    const confirmDeleteMonitoredLabel = async () => {
        handleDeleteMonitoredLabel(activeRecord);
        setActiveRecord(null);
        setModalState({ show: false, action: null });
    };

    const onSort = handleColumnSort(sortColumn, setSortColumn, sortDirection, setSortDirection);

    const columns = [
        {
            label: 'Name',
            id: 'name',
            onSort: () => onSort('name', DEFAULT_NAME_SORT_DIRECTION),
            render: (rowId, record) => <div key={rowId} className="text-base">{record.name}</div>,
        },
        folderContentsColumn,
        mailboxColumn,
        {
            label: 'Archival',
            id: 'archivalModeAndPeriod',
            render: (rowId, record) => {
                const message = (record.archivalMode === 'custom' && (
                    `${record.archivalPeriod} days`
                )) || (record.archivalMode === 'slow' && (
                    <i>Slow</i>
                )) || (record.archivalMode === 'fast' && (
                    <i>Fast</i>
                )) || (record.archivalMode === 'off' && (
                    'Off'
                )) || record.archivalMode;
                const tip = (record.archivalMode === 'slow' && (
                    user.slowArchivalPeriod
                        ? `${user.slowArchivalPeriod} days. This value can be adjusted in your profile.`
                        : 'Adjust this value in your profile.  Currently it\'s not set, so no messages will be archived.'
                )) || (record.archivalMode === 'fast' && (
                    user.fastArchivalPeriod
                        ? `${user.fastArchivalPeriod} days. This value can be adjusted in your profile.`
                        : 'Adjust this value in your profile.  Currently it\'s not set, so no messages will be archived.'
                )) || null;
                return (
                    <div
                        key={rowId}
                        data-tooltip-id="tip"
                        data-tooltip-content={tip}
                        className="text-base"
                    >
                        {message}
                    </div>
                );
            },
        },
        autopilotColumn,
        {
            label: '',
            id: 'editAction',
            render: (rowId, record) => (
                <div key={rowId} className="float-right pr-2">
                    <button
                        type="button"
                        data-tooltip-id="tip"
                        data-tooltip-content="Edit this Folder"
                        title="Edit this Folder"
                        aria-label="Edit this Folder"
                        className={`${styles.actionButton} linkLike--secondary`}
                        onClick={() => router.push({
                            pathname: routes.EDIT_MONITORED_LABELS,
                            query: { id: record.id },
                        })}
                    >
                        <Pencil className="h-4 w-4 text-progressBarBlue" />
                    </button>
                </div>
            ),
        },
        {
            label: '',
            id: 'deleteAction',
            render: (rowId, record) => (
                <div key={rowId} className="float-right pr-2">
                    <button
                        type="button"
                        data-tooltip-id="tip"
                        data-tooltip-content="Delete this Folder"
                        title="Delete this Folder"
                        aria-label="Delete this Folder"
                        className={`${styles.actionButton} linkLike--secondary`}
                        onClick={() => openDeleteMonitoredLabelModal(record)}
                    >
                        <TrashBin className="h-4 w-4" />
                    </button>
                </div>
            ),
        },
    ];

    const tableTitle = () => (
        <div className="flex gap-2 items-center justify-between w-full flex-wrap">
            <p>
                <b>Folders</b>
                {` (${monitoredLabels.length})`}
            </p>
            <Button
                size={ButtonSize.base}
                text="New folder"
                type={ButtonType.submit}
                onClick={() => {
                    router.push(routes.ADD_MONITORED_LABELS);
                }}
                icon={<Plus className="mr-2" />}
            />
        </div>
    );

    const externalStyles = {
        name: 'w-[20%]',
        folderContents: 'w-[20%]',
        account: 'w-[30%]',
        archivalModeAndPeriod: 'w-[10%]',
        autopilotColumn: 'w-[10%]',
        editAction: 'w-[5%]',
        deleteAction: 'w-[5%]',
    };

    return (
        <Loader isLoading={loading && !monitoredLabels} isError={isError}>

            <>
                <SmartTable
                    externalStyles={externalStyles}
                    title={tableTitle}
                    columns={columns}
                    data={monitoredLabels}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    paginationState={{ windowSize: 3, initialPage: 0 }}
                />
                <Tooltip id="tip" />
            </>

            <ConfirmActionModal
                modalType="agenda"
                element="Monitored label"
                display={modalState.show}
                onSubmit={() => confirmDeleteMonitoredLabel()}
                closeModal={() => {
                    setActiveRecord(null);
                    setModalState({ show: false, action: null });
                }}
                actionType={modalState.action || ''}
            />

        </Loader>
    );
}

export default MonitoredLabelsTable;
